/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { useRegion } from '@ibm-watson/shared/modules/ia/hooks/useRegion';
import {
  isFeatureEnabled,
  selectIsGenesysAudioConnectorInCpd
} from '@ibm-watson/shared/modules/ia/store/features/selectors';
import { selectPlan } from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import { useAppSelector } from '@ibm-watson/shared/store';
import { FeatureFlag } from '@ibm-watson/shared/ts/feature-flags';
import { isAws } from '../../ia/util/config';
import {
  supportedWebChatServiceDesks,
  supportedPhoneServiceDesks
} from '../helpers/serviceDeskHelpers';
import {
  channelCardFeatureFlags,
  smsFeatureFlags,
  INTELEPEER_SMS_TYPE,
  SMS_CHANNEL_TYPE,
  TEAMS_CHANNEL_TYPE,
  GENESYS_SMS_TYPE
} from '../util/channelTypes';

/**
 * A hook to return an object of supported web chat channels from
 * supported web chat feature flags.
 *
 * @returns {object} supported web chat channels
 */
export const useWebChatFeatures = () => {
  const serviceDeskFlags: FeatureFlag[] = [
    FeatureFlag.WEB_EXPERIENCE_SALESFORCE,
    FeatureFlag.WEB_EXPERIENCE_ZENDESKWEB
  ];
  const filteredServiceDeskFlags = useAppSelector(state => {
    return (
      serviceDeskFlags
        .filter(flag => isFeatureEnabled(state, flag))
        // convert to object where each filtered flag is true
        .reduce((obj, flag) => {
          obj[flag] = true;
          return obj;
        }, {})
    );
  });
  const plan = useAppSelector(selectPlan);
  return supportedWebChatServiceDesks(filteredServiceDeskFlags, plan);
};

/**
 * A hook to return an object of supported phone channels from
 * supported phone feature flags.
 *
 * @returns {object} supported phone channels
 */
export const usePhoneFeatures = () => {
  const isNewIA = useIsNewIA();

  const isAmazonConnectEnabled = useAppSelector(state =>
    isFeatureEnabled(state, FeatureFlag.PHONE_AMAZON_CONNECT)
  );

  const isGenesysAudioConnectorEnabledInCpd = useAppSelector(
    selectIsGenesysAudioConnectorInCpd
  );

  // Temporary arrangement to hide niceCXOne in aws environment till https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/65651#issuecomment-89564880
  // gets resolved
  const isNICECXoneEnabled = isNewIA && !isAws;
  const isGenesysAudioConnectorEnabled = isNewIA;
  const region = useRegion();
  const plan = useAppSelector(selectPlan);

  return supportedPhoneServiceDesks(
    // Hide amazonConnect in classicIA
    isNewIA && isAmazonConnectEnabled,
    isNICECXoneEnabled,
    // Hide genesysAudioConnector in classicIA
    isGenesysAudioConnectorEnabled,
    region,
    plan,
    isGenesysAudioConnectorEnabledInCpd
  );
};

/**
 * A hook to return an object of supported channels from feature flags.
 *
 * @returns {object} supported channels
 */
export const useChannelFeatures = () => {
  const isNewIA = useIsNewIA();

  let filteredChannels = useAppSelector(state =>
    Object.entries(channelCardFeatureFlags)
      // Channels without a feature flag or have the flag enabled
      // will pass.
      .filter(([_channel, flag]) => {
        if (!_.isEmpty(flag)) {
          return isFeatureEnabled(state, flag);
        }
        return true;
      })
      // Return only the key/channelType.
      .map(([channel, _flag]) => channel)
  );

  if (!isNewIA) {
    _.remove(filteredChannels, channel => channel === TEAMS_CHANNEL_TYPE);
  }

  if (filteredChannels.find(channel => channel === SMS_CHANNEL_TYPE)) {
    filteredChannels = _.uniq([SMS_CHANNEL_TYPE, ...filteredChannels]);
  }

  return filteredChannels;
};

/**
 * A hook to return an object of supported sms providers from
 * supported phone feature flags.
 *
 * @returns {object} supported sms providers
 */
export const useSMSFeatures = () => {
  // SMS with IntelePeer is only enabled in New IA
  const isSMSIntelepeerEnabled = useIsNewIA();
  // SMS with Genesys is enabled everywhere except for Classic IA and CP4D
  const isSMSGenesysEnabled = useIsNewIA() && !isCpd;

  const filteredSMSChannels = useAppSelector(state =>
    Object.entries(smsFeatureFlags)
      // Channels without a feature flag or have the flag enabled
      // will pass.
      .filter(([_channel, flag]) => {
        if (!_.isEmpty(flag)) {
          return isFeatureEnabled(state, flag);
        }
        return true;
      })
      // Return only the key/channelType.
      .map(([channel, _flag]) => channel)
  );

  if (!isSMSIntelepeerEnabled) {
    _.remove(filteredSMSChannels, channel => channel === INTELEPEER_SMS_TYPE);
  }

  if (!isSMSGenesysEnabled) {
    _.remove(filteredSMSChannels, channel => channel === GENESYS_SMS_TYPE);
  }
  return filteredSMSChannels;
};
